import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Check, Eye, Mail, Megaphone, Pencil, Plus, Trash2, X } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { displayName, formatDate, localDate } from '@/lib/format'
import { copyUpdateToClipboard, updateMailtoHref } from '@/lib/updates'
import type { Profile, TeamUpdate } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogDescription,
  DialogFullScreenContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState, ErrorNote, Loading, Markdown, Page } from '@/components/page'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

/**
 * Updates — the weekly note DPL sends a team (Towel #9,
 * migrations/0071_team_updates.sql).
 *
 * A DPL coach writes it in Markdown; everyone on the team reads it here. That
 * audience is the opposite of Reports and Coaching on purpose: this is the same
 * text the team gets by email. The coach-only controls below are courtesy —
 * RLS refuses a write from anyone who is not a DPL coach of the team.
 */
export function UpdatesPage() {
  const { currentTeam, isCoach } = useAuth()
  const teamId = currentTeam?.id
  const navigate = useNavigate()
  const [creating, setCreating] = React.useState(false)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!teamId) return [] as TeamUpdate[]
    const { data: rows, error: err } = await sb()
      .from('team_updates')
      .select('*')
      .eq('team_id', teamId)
      .order('sent_on', { ascending: false })
      .order('created_at', { ascending: false })
    if (err) throw err
    return (rows ?? []) as TeamUpdate[]
  }, [teamId])

  async function remove(u: TeamUpdate) {
    if (!window.confirm(`Delete “${u.title}”? This can't be undone.`)) return
    const { error: err } = await sb().from('team_updates').delete().eq('id', u.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  const updates = data ?? []

  return (
    <Page
      title="Updates"
      description={
        isCoach
          ? 'The weekly note you send this team, written in Markdown. Everyone on the team can read these.'
          : 'Weekly updates from your Don’t Panic Labs coach.'
      }
      actions={
        isCoach && (
          <Button onClick={() => setCreating(true)}>
            <Plus /> New update
          </Button>
        )
      }
    >
      {error && <ErrorNote error={error} />}

      {loading && !data ? (
        <Loading />
      ) : updates.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="No updates yet"
          description={
            isCoach
              ? 'Write this week’s recap: the takeaways, what’s up next, a tip. Then copy it into an email with Email Update.'
              : 'Your coach hasn’t posted an update for this team yet.'
          }
          action={
            isCoach && (
              <Button onClick={() => setCreating(true)}>
                <Plus /> Write an update
              </Button>
            )
          }
        />
      ) : (
        <div className="space-y-2.5">
          {updates.map((u) => (
            <Card key={u.id} className="transition-colors hover:border-primary/40">
              <CardContent className="flex flex-wrap items-center gap-4 p-4">
                <div className="min-w-0 flex-1">
                  <Link to={`/updates/${u.id}`} className="font-medium hover:underline">
                    {u.title}
                  </Link>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="font-medium">{formatDate(u.sent_on)}</span>
                    {u.subtitle && <span className="truncate">{u.subtitle}</span>}
                  </div>
                </div>
                {isCoach && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => void remove(u)}
                    aria-label={`Delete ${u.title}`}
                  >
                    <Trash2 />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {creating && teamId && (
        <UpdateEditor
          teamId={teamId}
          onClose={() => setCreating(false)}
          onSaved={(saved) => {
            setCreating(false)
            navigate(`/updates/${saved.id}`)
          }}
        />
      )}
    </Page>
  )
}

/** "Tue, Nov 3, 2026" — the week an update is about, written out. */
function longDate(iso: string): string {
  return localDate(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function UpdateDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { isCoach } = useAuth()
  const [editing, setEditing] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const [copyError, setCopyError] = React.useState<string | null>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!id) return null
    const { data: row, error: err } = await sb().from('team_updates').select('*').eq('id', id).maybeSingle()
    if (err) throw err
    if (!row) return null
    const update = row as TeamUpdate
    // The author's name is a nicety: a profile the reader cannot see just
    // leaves the byline as "Don't Panic Labs".
    let author: Pick<Profile, 'display_name' | 'email'> | null = null
    if (update.created_by) {
      const { data: prof } = await sb()
        .from('profiles')
        .select('display_name, email')
        .eq('user_id', update.created_by)
        .maybeSingle()
      author = (prof as Pick<Profile, 'display_name' | 'email'> | null) ?? null
    }
    return { update, author }
  }, [id])

  // "Copied" goes back to "Email Update" after a few seconds, so the button
  // never claims the clipboard still holds this update after it may not.
  React.useEffect(() => {
    if (!copied) return
    const t = window.setTimeout(() => setCopied(false), 6000)
    return () => window.clearTimeout(t)
  }, [copied])

  function emailUpdate(update: TeamUpdate) {
    setCopyError(null)
    // Started synchronously inside the click: the clipboard write needs the
    // user gesture (see lib/updates.ts).
    copyUpdateToClipboard(update)
      .then(() => {
        setCopied(true)
        window.location.href = updateMailtoHref(update)
      })
      .catch((err: unknown) => setCopyError(err instanceof Error ? err.message : String(err)))
  }

  if (loading && !data) return <Loading />
  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <ErrorNote error={error} />
      </div>
    )
  }
  if (!data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="text-sm text-muted-foreground">That update doesn&apos;t exist, or it isn&apos;t on this team.</p>
        <Button asChild variant="link" className="mt-2">
          <Link to="/updates">Back to updates</Link>
        </Button>
      </div>
    )
  }

  const { update, author } = data

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 lg:py-8">
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/updates">
            <ArrowLeft /> Updates
          </Link>
        </Button>
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Eye className="h-3.5 w-3.5" /> the whole team can read this
        </span>
        <div className="flex-1" />
        {isCoach && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <Pencil /> Edit
          </Button>
        )}
        <Button size="sm" onClick={() => emailUpdate(update)}>
          {copied ? (
            <>
              <Check /> Copied
            </>
          ) : (
            <>
              <Mail /> Email Update
            </>
          )}
        </Button>
      </div>

      {copied && (
        <p className="mb-4 rounded-md border border-dashed bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          The update is on your clipboard as formatted HTML. Your email client is opening with the subject filled
          in — paste into the body.
        </p>
      )}
      {copyError && (
        <div className="mb-4">
          <ErrorNote error={copyError} />
        </div>
      )}

      <article className="rounded-lg border bg-card p-6 shadow-sm sm:p-10">
        <header className="border-b pb-5">
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-primary">{update.title}</h1>
          {update.subtitle && (
            <p className="mt-1 text-lg font-medium leading-snug text-primary/85">{update.subtitle}</p>
          )}
          <p className="mt-1.5 text-xs text-muted-foreground">
            {longDate(update.sent_on)} · written by{' '}
            {author ? `${displayName(author)}, Don’t Panic Labs` : 'Don’t Panic Labs'}
          </p>
        </header>
        <section className="mt-6">
          {update.body.trim() ? (
            <Markdown source={update.body} />
          ) : (
            <p className="text-sm italic text-muted-foreground">Nothing written yet.</p>
          )}
        </section>
      </article>

      {editing && (
        <UpdateEditor
          teamId={update.team_id}
          update={update}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false)
            await refetch()
          }}
        />
      )}
    </div>
  )
}

/** Today as `YYYY-MM-DD` in local time — never `toISOString`, which is UTC. */
function todayIso(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * The editor: full screen, with the Markdown on one side and how it will read
 * on the other. Full screen for the same reason as the meeting notes (0035):
 * this is writing, and a narrow dialog with the page showing round it is what
 * sends people to write it somewhere else and paste it in.
 */
function UpdateEditor({
  teamId,
  update,
  onClose,
  onSaved,
}: {
  teamId: string
  update?: TeamUpdate
  onClose: () => void
  onSaved: (saved: TeamUpdate) => void | Promise<void>
}) {
  const [title, setTitle] = React.useState(update?.title ?? '')
  const [subtitle, setSubtitle] = React.useState(update?.subtitle ?? '')
  const [sentOn, setSentOn] = React.useState(update?.sent_on ?? todayIso())
  const [body, setBody] = React.useState(update?.body ?? '')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const guard = useDiscardGuard(useDirty({ title, subtitle, sentOn, body }), onClose)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return setError('Give the update a title.')
    setBusy(true)
    setError(null)
    const fields = {
      title: title.trim(),
      subtitle: subtitle.trim() || null,
      sent_on: sentOn || todayIso(),
      body,
    }
    try {
      const query = update
        ? sb().from('team_updates').update(fields).eq('id', update.id)
        : sb().from('team_updates').insert({ ...fields, team_id: teamId })
      const { data: saved, error: err } = await query.select().single()
      if (err) throw err
      await onSaved(saved as TeamUpdate)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogFullScreenContent>
        <DiscardPrompt guard={guard} what="this update" />
        <form onSubmit={save} className="flex h-full flex-col">
          <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3 sm:px-6">
            <div className="min-w-0">
              <DialogTitle className="truncate text-base">{update ? 'Edit update' : 'New update'}</DialogTitle>
              <DialogDescription className="truncate text-xs">
                Written in Markdown. Everyone on the team can read it once it&apos;s saved.
              </DialogDescription>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <Button type="button" variant="ghost" onClick={guard.requestClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {update ? 'Save changes' : 'Save update'}
              </Button>
              <Button type="button" variant="ghost" size="icon-sm" onClick={guard.requestClose} aria-label="Close">
                <X />
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_11rem]">
                  <div className="space-y-2">
                    <Label htmlFor="utitle">Title</Label>
                    <Input
                      id="utitle"
                      autoFocus
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Week of November 3"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="udate">Week of</Label>
                    <Input id="udate" type="date" value={sentOn} onChange={(e) => setSentOn(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="usub">Subtitle</Label>
                  <Input
                    id="usub"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Optional — usually the session this recaps"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ubody">Update</Label>
                  <Textarea
                    id="ubody"
                    rows={22}
                    className="font-mono text-[13px]"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder={'Hi team,\n\n## Key takeaways\n- …\n\n| Monolith | Microservices |\n| --- | --- |\n| Simple to run | Independent deploys |'}
                  />
                </div>
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>

              <div className="min-w-0 space-y-2">
                <Label>Preview</Label>
                <div className="rounded-lg border bg-card p-5">
                  {body.trim() ? (
                    <Markdown source={body} />
                  ) : (
                    <p className="text-sm italic text-muted-foreground">The update will appear here as you write.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </DialogFullScreenContent>
    </Dialog>
  )
}
